// Enhanced Offline Storage Service using Dexie.js
// Provides robust IndexedDB storage with sync capabilities

import Dexie from 'dexie';

// Define the database schema
class BeautyFinanceDB extends Dexie {
  constructor() {
    super('BeautyFinanceDB');
    
    this.version(1).stores({
      transactions: '++id, date, amount, category, description, paymentMethod, service, vatRate, vatAmount, vatComponents, syncStatus, createdAt, updatedAt',
      depreciation: '++id, transactionId, period, syncStatus, createdAt, updatedAt',
      servicePrices: '++id, category, name, duration, cost, price, syncStatus, createdAt, updatedAt',
      syncQueue: '++id, type, data, attempts, lastAttempt, error',
      settings: '++id, key, value, syncStatus, updatedAt'
    });

    // Add missing syncQueue indexes for ordering and operation metadata.
    // This fixes Dexie errors when ordering by createdAt on older schemas.
    this.version(2).stores({
      transactions: '++id, date, amount, category, description, paymentMethod, service, vatRate, vatAmount, vatComponents, syncStatus, createdAt, updatedAt',
      depreciation: '++id, transactionId, period, syncStatus, createdAt, updatedAt',
      servicePrices: '++id, category, name, duration, cost, price, syncStatus, createdAt, updatedAt',
      syncQueue: '++id, type, operation, attempts, lastAttempt, error, createdAt',
      settings: '++id, key, value, syncStatus, updatedAt'
    });
  }
}

// Initialize the database
const db = new BeautyFinanceDB();

// Sync status constants
export const SYNC_STATUS = {
  SYNCED: 'synced',
  PENDING: 'pending',
  FAILED: 'failed',
  LOCAL_ONLY: 'local_only'
};

// Transaction operations
export const transactionStorage = {
  // Add a new transaction
  async add(transaction) {
    const now = new Date().toISOString();
    const transactionWithMeta = {
      ...transaction,
      id: transaction.id || `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      syncStatus: SYNC_STATUS.PENDING,
      createdAt: now,
      updatedAt: now
    };
    
    try {
      const id = await db.transactions.add(transactionWithMeta);
      await addToSyncQueue('transaction', 'create', transactionWithMeta);
      return { ...transactionWithMeta, id };
    } catch (error) {
      console.error('Failed to add transaction to offline storage:', error);
      throw error;
    }
  },

  // Get all transactions
  async getAll() {
    try {
      return await db.transactions.orderBy('date').reverse().toArray();
    } catch (error) {
      console.error('Failed to get transactions from offline storage:', error);
      return [];
    }
  },

  // Update a transaction
  async update(id, updates) {
    const now = new Date().toISOString();
    const updateData = {
      ...updates,
      updatedAt: now,
      syncStatus: SYNC_STATUS.PENDING
    };
    
    try {
      await db.transactions.update(id, updateData);
      const updatedTransaction = await db.transactions.get(id);
      await addToSyncQueue('transaction', 'update', updatedTransaction);
      return updatedTransaction;
    } catch (error) {
      console.error('Failed to update transaction in offline storage:', error);
      throw error;
    }
  },

  // Delete a transaction
  async delete(id) {
    try {
      const transaction = await db.transactions.get(id);
      if (transaction) {
        await db.transactions.delete(id);
        await addToSyncQueue('transaction', 'delete', { id });
      }
    } catch (error) {
      console.error('Failed to delete transaction from offline storage:', error);
      throw error;
    }
  },

  // Mark transaction as synced
  async markSynced(id) {
    try {
      await db.transactions.update(id, { 
        syncStatus: SYNC_STATUS.SYNCED,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to mark transaction as synced:', error);
    }
  },

  // Get pending sync transactions
  async getPendingSync() {
    try {
      return await db.transactions
        .where('syncStatus')
        .equals(SYNC_STATUS.PENDING)
        .toArray();
    } catch (error) {
      console.error('Failed to get pending sync transactions:', error);
      return [];
    }
  }
};

// Service prices operations
export const servicePricesStorage = {
  async save(servicePrices) {
    const now = new Date().toISOString();
    
    try {
      // Clear existing service prices
      await db.servicePrices.clear();
      
      // Add new service prices
      const servicePricesArray = [];
      Object.entries(servicePrices).forEach(([category, services]) => {
        services.forEach(service => {
          servicePricesArray.push({
            ...service,
            category,
            id: `${category}_${service.name}_${Date.now()}`,
            syncStatus: SYNC_STATUS.PENDING,
            createdAt: now,
            updatedAt: now
          });
        });
      });
      
      await db.servicePrices.bulkAdd(servicePricesArray);
      await addToSyncQueue('servicePrices', 'update', servicePrices);
      
      return servicePrices;
    } catch (error) {
      console.error('Failed to save service prices to offline storage:', error);
      throw error;
    }
  },

  async load() {
    try {
      const services = await db.servicePrices.toArray();
      const servicePrices = {};
      
      services.forEach(service => {
        if (!servicePrices[service.category]) {
          servicePrices[service.category] = [];
        }
        servicePrices[service.category].push({
          name: service.name,
          duration: service.duration,
          cost: service.cost,
          price: service.price
        });
      });
      
      return servicePrices;
    } catch (error) {
      console.error('Failed to load service prices from offline storage:', error);
      return {};
    }
  }
};

// Depreciation operations
export const depreciationStorage = {
  async save(depreciationPeriods) {
    const now = new Date().toISOString();
    
    try {
      // Clear existing depreciation data
      await db.depreciation.clear();
      
      // Add new depreciation data
      const depreciationArray = Object.entries(depreciationPeriods).map(([transactionId, period]) => ({
        transactionId,
        period,
        syncStatus: SYNC_STATUS.PENDING,
        createdAt: now,
        updatedAt: now
      }));
      
      await db.depreciation.bulkAdd(depreciationArray);
      await addToSyncQueue('depreciation', 'update', depreciationPeriods);
      
      return depreciationPeriods;
    } catch (error) {
      console.error('Failed to save depreciation to offline storage:', error);
      throw error;
    }
  },

  async load() {
    try {
      const depreciationData = await db.depreciation.toArray();
      const depreciationPeriods = {};
      
      depreciationData.forEach(item => {
        depreciationPeriods[item.transactionId] = item.period;
      });
      
      return depreciationPeriods;
    } catch (error) {
      console.error('Failed to load depreciation from offline storage:', error);
      return {};
    }
  }
};

// Settings operations
export const settingsStorage = {
  async set(key, value) {
    const now = new Date().toISOString();
    
    try {
      await db.settings.put({
        key,
        value: JSON.stringify(value),
        syncStatus: SYNC_STATUS.LOCAL_ONLY, // Settings are usually local-only
        updatedAt: now
      });
    } catch (error) {
      console.error('Failed to save setting to offline storage:', error);
      throw error;
    }
  },

  async get(key, defaultValue = null) {
    try {
      const setting = await db.settings.where('key').equals(key).first();
      return setting ? JSON.parse(setting.value) : defaultValue;
    } catch (error) {
      console.error('Failed to get setting from offline storage:', error);
      return defaultValue;
    }
  },

  async remove(key) {
    try {
      await db.settings.where('key').equals(key).delete();
    } catch (error) {
      console.error('Failed to remove setting from offline storage:', error);
    }
  }
};

// Sync queue operations
async function addToSyncQueue(type, operation, data) {
  try {
    await db.syncQueue.add({
      type,
      operation,
      data,
      attempts: 0,
      lastAttempt: null,
      error: null,
      createdAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to add item to sync queue:', error);
  }
}

export const syncQueue = {
  async getAll() {
    try {
      // Use toArray + JS sort so this works even on older indexedDB schemas
      // where createdAt is not yet indexed.
      const items = await db.syncQueue.toArray();
      return items.sort((a, b) => {
        const aTime = new Date(a?.createdAt || 0).getTime();
        const bTime = new Date(b?.createdAt || 0).getTime();
        return aTime - bTime;
      });
    } catch (error) {
      console.error('Failed to get sync queue:', error);
      return [];
    }
  },

  async remove(id) {
    try {
      await db.syncQueue.delete(id);
    } catch (error) {
      console.error('Failed to remove item from sync queue:', error);
    }
  },

  async updateAttempt(id, error = null) {
    try {
      const item = await db.syncQueue.get(id);
      if (item) {
        await db.syncQueue.update(id, {
          attempts: item.attempts + 1,
          lastAttempt: new Date().toISOString(),
          error: error ? error.message : null
        });
      }
    } catch (error) {
      console.error('Failed to update sync queue item:', error);
    }
  },

  async clear() {
    try {
      await db.syncQueue.clear();
    } catch (error) {
      console.error('Failed to clear sync queue:', error);
    }
  }
};

// Database utilities
export const dbUtils = {
  async getStorageInfo() {
    try {
      const transactionCount = await db.transactions.count();
      const servicePricesCount = await db.servicePrices.count();
      const depreciationCount = await db.depreciation.count();
      const syncQueueCount = await db.syncQueue.count();
      const settingsCount = await db.settings.count();
      
      return {
        transactions: transactionCount,
        servicePrices: servicePricesCount,
        depreciation: depreciationCount,
        syncQueue: syncQueueCount,
        settings: settingsCount,
        totalRecords: transactionCount + servicePricesCount + depreciationCount + syncQueueCount + settingsCount
      };
    } catch (error) {
      console.error('Failed to get storage info:', error);
      return null;
    }
  },

  async clearAllData() {
    try {
      await db.delete();
      await db.open();
      console.log('All offline data cleared');
    } catch (error) {
      console.error('Failed to clear all data:', error);
      throw error;
    }
  },

  async exportData() {
    try {
      const transactions = await db.transactions.toArray();
      const servicePrices = await db.servicePrices.toArray();
      const depreciation = await db.depreciation.toArray();
      const settings = await db.settings.toArray();
      
      return {
        transactions,
        servicePrices,
        depreciation,
        settings,
        exportDate: new Date().toISOString()
      };
    } catch (error) {
      console.error('Failed to export data:', error);
      throw error;
    }
  }
};

// Connection status monitoring
export const connectionMonitor = {
  isOnline: navigator.onLine,
  
  init() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.onConnectionChange(true);
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.onConnectionChange(false);
    });
  },
  
  onConnectionChange(isOnline) {
    console.log(`Connection status changed: ${isOnline ? 'online' : 'offline'}`);
    
    if (isOnline) {
      // Trigger sync when back online
      this.triggerSync();
    }
  },
  
  async triggerSync() {
    // This will be implemented to sync with Firebase/Google Drive
    console.log('Triggering sync...');
    
    try {
      const pendingItems = await syncQueue.getAll();
      console.log(`Found ${pendingItems.length} items to sync`);
      
      // Process sync queue (implementation depends on your sync strategy)
      for (const item of pendingItems) {
        try {
          // Sync logic would go here
          await syncQueue.remove(item.id);
        } catch (error) {
          await syncQueue.updateAttempt(item.id, error);
        }
      }
    } catch (error) {
      console.error('Sync failed:', error);
    }
  }
};

// Initialize connection monitoring
connectionMonitor.init();

export default db;
